import { format } from "date-fns";
import { ar, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";

import { CreateProfessionalJob } from "../../../../../types/createJobSchema";
import {
  JobEndDateIcon,
  JobModeIcon,
  ProfessionalJobIcon,
  SalaryIcon,
} from "../../../../../assets/icons/go-hire-page-icons";

const ProfessionalInfo = ({ jobDetails }: { jobDetails: CreateProfessionalJob }) => {
  const { t, i18n } = useTranslation("goHire", { keyPrefix: "createJobPage.previewAndPublishJob" });
  const { t: tGoHireEnum } = useTranslation("goHire", { keyPrefix: "enum" });
  const { t: tUserRole } = useTranslation("common", { keyPrefix: "user.role" });
  const selectedLocale = i18n.language === "ar" ? ar : enUS;

  const professionalInfo = {
    jobName: jobDetails.jobName,
    position: jobDetails.position,
    jobMode: jobDetails.jobMode,
    subRole: jobDetails.subRole,
    salaryAmount: jobDetails.salaryAmount,
    salaryCurrency: jobDetails.salaryCurrency,
    salaryPer: jobDetails.salaryPer,
    applyEnds: jobDetails.applyEnds,
  };

  const perValue = tGoHireEnum(`salaryPer.${professionalInfo.salaryPer}`);
  const playerInfoList = [
    {
      icon: <ProfessionalJobIcon />,
      title: professionalInfo.subRole
        ? `${tUserRole(professionalInfo.position)} - ${tUserRole(professionalInfo.subRole)}`
        : tUserRole(professionalInfo.position),
    },
    {
      icon: <JobModeIcon />,
      title: tGoHireEnum(`jobMode.${professionalInfo.jobMode}`),
    },
    {
      icon: <SalaryIcon />,
      title: t("salary", {
        salary: professionalInfo.salaryAmount,
        currency: professionalInfo.salaryCurrency,
        per: perValue,
      }),
    },
    {
      icon: <JobEndDateIcon />,
      title: t("endIn", { date: format(professionalInfo.applyEnds, "dd MMM yyyy", { locale: selectedLocale }) }),
    },
  ];

  return (
    <Box component="section" sx={styles.container}>
      <Typography sx={styles.title}>{professionalInfo.jobName}</Typography>
      <Box sx={styles.listContainer}>
        {playerInfoList.map(
          ({ icon, title }) =>
            !!title && (
              <Box sx={styles.listItem}>
                {icon}
                <Typography sx={styles.listItemTitle}>{title}</Typography>
              </Box>
            )
        )}
      </Box>
    </Box>
  );
};

export default ProfessionalInfo;

const styles = {
  container: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  listContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  listItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  title: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  listItemTitle: {
    fontFamily: "Montserrat",
    color: "var(--Dark-Mode---100, #1E2021)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "24px",
  },
};
